import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMediaUrl, getToken, requestJson } from '../api';

const SIZE_OPTIONS = {
  men: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'],
  women: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  kids: ['2-3Y', '4-5Y', '6-7Y', '8-9Y', '10-11Y', '12-13Y'],
  sports: ['S', 'M', 'L', 'XL', 'XXL'],
};

const resolveSizeOptions = (category) => {
  const c = String(category || '').toLowerCase();
  if (c.includes('women') || c.includes('ladies') || c.includes('female')) return SIZE_OPTIONS.women;
  if (c.includes('kids') || c.includes('kid') || c.includes('boys') || c.includes('girls')) return SIZE_OPTIONS.kids;
  if (c.includes('sports')) return SIZE_OPTIONS.sports;
  if (c.includes('men') || c.includes('male')) return SIZE_OPTIONS.men;
  return [];
};

const normalizeVariantForEdit = (variant) => {
  const base = variant && typeof variant === 'object' ? variant : {};
  let sizes = base.sizes;
  if (sizes === undefined || sizes === null) sizes = base.size;
  if (typeof sizes === 'string') {
    sizes = sizes
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(sizes)) sizes = [];

  let color = base.color;
  if (!color && Array.isArray(base.colors) && base.colors.length) {
    color = base.colors[0];
  }
  color = String(color || '').trim();

  const packRaw = Number(base.pack_count ?? base.quantity ?? 0);
  const packCount = Number.isFinite(packRaw) ? packRaw : 0;
  let sizeQuantities = base.size_quantities && typeof base.size_quantities === 'object'
    ? base.size_quantities
    : {};
  if (sizes.length && packCount > 0) {
    const per = Math.floor(packCount / sizes.length);
    const remainder = packCount % sizes.length;
    sizeQuantities = {};
    sizes.forEach((size, idx) => {
      sizeQuantities[size] = per + (idx < remainder ? 1 : 0);
    });
  }

  return {
    ...base,
    color,
    colors: color ? [color] : [],
    sizes,
    pack_count: packCount,
    quantity: packCount,
    size_quantities: sizeQuantities,
  };
};

const inferImageFileName = (imageValue, idx, mimeType) => {
  const raw = String(imageValue || '')
    .split('/')
    .filter(Boolean)
    .pop() || `gallery-${idx + 1}`;
  if (/\.[a-z0-9]+$/i.test(raw)) return raw;
  const typePart = String(mimeType || '')
    .split(';')[0]
    .split('/')[1];
  const ext = typePart || 'jpg';
  return `${raw}.${ext}`;
};

const displayImageName = (imageValue, idx) =>
  String(imageValue || '')
    .split('/')
    .filter(Boolean)
    .pop() || `Image ${idx + 1}`;

const ProductEditPage = ({ updateProduct, categories = [], subCategories = [] }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    name: '',
    brand: '',
    category: '',
    sub_category: '',
    description: '',
    main_price: '',
    discount_percentage: '',
    quantity: '',
    colors: '',
    main_image: '',
    images: [],
    variants: [],
  });
  const [mainImageFile, setMainImageFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [variantColorFiles, setVariantColorFiles] = useState({});
  const [galleryTouched, setGalleryTouched] = useState(false);
  const filteredSubCategories = (subCategories || []).filter(
    (s) =>
      String(s?.category_name || '').trim().toLowerCase() ===
      String(form.category || '').trim().toLowerCase()
  );
  const sizeOptions = useMemo(() => resolveSizeOptions(form.category), [form.category]);

  const applyProduct = (data) => {
    setForm({
      name: data?.name || '',
      brand: data?.brand || '',
      category: data?.category || '',
      sub_category: data?.sub_category || '',
      description: data?.description || '',
      main_price: data?.main_price ?? '',
      discount_percentage: data?.discount_percentage ?? '',
      quantity: data?.quantity ?? '',
      colors: Array.isArray(data?.colors) ? data.colors.join(', ') : (data?.colors || ''),
      main_image: data?.main_image || '',
      images: Array.isArray(data?.images) ? data.images : [],
      variants: Array.isArray(data?.variants) ? data.variants.map(normalizeVariantForEdit) : [],
    });
    setMainImageFile(null);
    setGalleryFiles([]);
    setVariantColorFiles({});
    setGalleryTouched(false);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await requestJson('GET', `/api/products/${id}/`, null, false);
        applyProduct(data);
      } catch (e) {
        setMessage(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const setVariantColorFile = (variantIndex, file) => {
    const key = String(variantIndex);
    setVariantColorFiles((prev) => ({ ...prev, [key]: file || null }));
  };

  const updateVariant = (index, key, value) => {
    setForm((prev) => {
      const currentVariants = Array.isArray(prev.variants) ? prev.variants : [];
      const nextVariants = currentVariants.map((variant, variantIndex) =>
        variantIndex === index
          ? normalizeVariantForEdit({ ...variant, [key]: value })
          : variant
      );
      return { ...prev, variants: nextVariants };
    });
  };

  const toggleSize = (index, size) => {
    const current = Array.isArray(form.variants?.[index]?.sizes) ? form.variants[index].sizes : [];
    const nextSizes = current.includes(size)
      ? current.filter((s) => s !== size)
      : [...current, size];
    updateVariant(index, 'sizes', nextSizes);
  };

  const addVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [
        ...(Array.isArray(prev.variants) ? prev.variants : []),
        normalizeVariantForEdit({
          color: '',
          colors: [],
          sizes: [],
          pack_count: 0,
          quantity: 0,
          price_per_piece: prev.main_price || 0,
        }),
      ],
    }));
  };

  const removeVariant = (index) => {
    setForm((prev) => ({
      ...prev,
      variants: (Array.isArray(prev.variants) ? prev.variants : []).filter((_, i) => i !== index),
    }));
    setVariantColorFiles((prev) => {
      const next = {};
      Object.entries(prev).forEach(([rawIdx, file]) => {
        const currentIdx = Number(rawIdx);
        if (!Number.isFinite(currentIdx) || !file) return;
        if (currentIdx < index) next[String(currentIdx)] = file;
        if (currentIdx > index) next[String(currentIdx - 1)] = file;
      });
      return next;
    });
  };

  const handleGalleryChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setGalleryFiles((prev) => [...prev, ...files]);
    setGalleryTouched(true);
    event.target.value = '';
  };

  const removeExistingGalleryImage = (index) => {
    setForm((prev) => ({
      ...prev,
      images: (Array.isArray(prev.images) ? prev.images : []).filter((_, i) => i !== index),
    }));
    setGalleryTouched(true);
  };

  const removeNewGalleryImage = (index) => {
    setGalleryFiles((prev) => prev.filter((_, i) => i !== index));
    setGalleryTouched(true);
  };

  const newGalleryPreviews = useMemo(
    () =>
      galleryFiles.map((file, idx) => ({
        idx,
        name: file?.name || `New ${idx + 1}`,
        url: URL.createObjectURL(file),
      })),
    [galleryFiles]
  );

  useEffect(
    () => () => {
      newGalleryPreviews.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
    },
    [newGalleryPreviews]
  );

  const fetchExistingGalleryFile = async (imageValue, idx) => {
    const token = getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await fetch(getMediaUrl(imageValue), { headers });
    if (!response.ok) {
      throw new Error(`Unable to load gallery image ${idx + 1} for update`);
    }
    const blob = await response.blob();
    return new File([blob], inferImageFileName(imageValue, idx, blob.type), {
      type: blob.type || 'application/octet-stream',
    });
  };

  const handleSave = async () => {
    setMessage('');
    const multipart = new FormData();

    const appendIfPresent = (key, value) => {
      if (value === undefined || value === null || value === '') return;
      multipart.append(key, value);
    };

    appendIfPresent('name', form.name);
    appendIfPresent('brand', form.brand);
    appendIfPresent('category', form.category);
    appendIfPresent('sub_category', form.sub_category);
    appendIfPresent('description', form.description);
    appendIfPresent('main_price', form.main_price);
    appendIfPresent('discount_percentage', form.discount_percentage);
    appendIfPresent('quantity', form.quantity);

    const variantsPayload = (form.variants || []).map((rawVariant) => {
      const variant = normalizeVariantForEdit(rawVariant);
      const color = String(variant.color || '').trim();
      return {
        ...variant,
        color: color || undefined,
        colors: color ? [color] : [],
        pack_count: Number(variant.pack_count || variant.quantity || 0),
        quantity: Number(variant.pack_count || variant.quantity || 0),
        color_input: undefined,
        __variant_index: undefined,
      };
    });
    multipart.append('variants', JSON.stringify(variantsPayload));

    const directColors = String(form.colors || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    const variantColors = variantsPayload
      .map((variant) => String(variant?.color || '').trim())
      .filter(Boolean);
    const allColors = Array.from(new Set([...directColors, ...variantColors]));
    allColors.forEach((color) => multipart.append('colors', color));

    if (mainImageFile) {
      multipart.append('main_image', mainImageFile);
    }

    if (galleryTouched) {
      const existingGallery = Array.isArray(form.images) ? form.images : [];
      if (!existingGallery.length && !galleryFiles.length) {
        setMessage('Keep at least one gallery image or upload a replacement before saving.');
        return;
      }
      const existingFiles = [];
      for (let i = 0; i < existingGallery.length; i += 1) {
        try {
          const file = await fetchExistingGalleryFile(existingGallery[i], i);
          existingFiles.push(file);
        } catch (e) {
          setMessage(e.message || 'Failed to process gallery images');
          return;
        }
      }
      [...existingFiles, ...galleryFiles].forEach((file) => multipart.append('images', file));
    }

    Object.entries(variantColorFiles).forEach(([variantIndexKey, file]) => {
      if (!file) return;
      const variantIndex = Number(variantIndexKey);
      if (!Number.isFinite(variantIndex)) return;
      const variant = form.variants?.[variantIndex];
      const color = String(variant?.color || '').trim().toLowerCase();
      if (!color) return;
      multipart.append(
        `variant_color_image_${variantIndex}__${encodeURIComponent(color)}`,
        file
      );
    });

    try {
      await updateProduct(id, multipart);
      const refreshed = await requestJson('GET', `/api/products/${id}/`, null, false);
      applyProduct(refreshed);
      setMessage('Product updated.');
    } catch (e) {
      setMessage(e.message || 'Update failed');
    }
  };

  if (loading) {
    return <div className="card">Loading...</div>;
  }

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3>Edit Product</h3>
        <button className="secondary" onClick={() => navigate('/admin/products')}>Back to Products</button>
      </div>

      <div className="grid">
        <div><label>Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><label>Brand</label><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
        <div>
          <label>Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value, sub_category: '' })}
          >
            <option value="">Select Category</option>
            {(categories || []).map((c) => (
              <option key={c._id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Sub Category</label>
          <select
            value={form.sub_category}
            onChange={(e) => setForm({ ...form, sub_category: e.target.value })}
            disabled={!form.category}
          >
            <option value="">Select Sub Category</option>
            {filteredSubCategories.map((s) => (
              <option key={s._id} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Price Per Piece</label>
          <input
            type="number"
            value={form.main_price}
            onChange={(e) => setForm({ ...form, main_price: e.target.value })}
          />
        </div>
        <div><label>Discount %</label><input type="number" value={form.discount_percentage} onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })} /></div>
        <div><label>Quantity</label><input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
        <div><label>Colors</label><input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} /></div>
      </div>

      <div>
        <label>Description</label>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <h4>Main Image</h4>
        {form.main_image ? (
          <div className="product-thumb" style={{ marginBottom: 8 }}>
            <img src={getMediaUrl(form.main_image)} alt="main" />
          </div>
        ) : (
          <div className="muted">No main image</div>
        )}
        <input type="file" accept="image/*" onChange={(e) => setMainImageFile(e.target.files?.[0] || null)} />
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <h4>Gallery Images</h4>
        {(form.images || []).length || newGalleryPreviews.length ? (
          <div className="product-gallery-grid">
            {(form.images || []).map((img, idx) => (
              <div key={`existing-gallery-${idx}`} className="product-gallery-item">
                <div className="product-gallery-thumb">
                  <img src={getMediaUrl(img)} alt={displayImageName(img, idx)} />
                </div>
                <div className="product-gallery-name">{displayImageName(img, idx)}</div>
                <button
                  type="button"
                  className="secondary product-gallery-remove"
                  onClick={() => removeExistingGalleryImage(idx)}
                >
                  Remove
                </button>
              </div>
            ))}
            {newGalleryPreviews.map((item) => (
              <div key={`new-gallery-${item.idx}`} className="product-gallery-item">
                <div className="product-gallery-thumb">
                  {item.url ? <img src={item.url} alt={item.name} /> : <span>No preview</span>}
                </div>
                <div className="product-gallery-name">{item.name}</div>
                <button
                  type="button"
                  className="secondary product-gallery-remove"
                  onClick={() => removeNewGalleryImage(item.idx)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="muted">No gallery images</div>
        )}
        <div className="muted" style={{ marginBottom: 6 }}>
          Upload new images to add, and remove any image before saving.
        </div>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleGalleryChange}
        />
      </div>

      <div className="card product-variant-theme" style={{ marginTop: 12 }}>
        <div className="row product-create-section-head">
          <h4>Variants</h4>
          <button type="button" className="secondary" onClick={addVariant}>Add Variant</button>
        </div>
        {(form.variants || []).length === 0 ? (
          <div className="muted">No variants found.</div>
        ) : (
          <div className="list">
            {form.variants.map((variant, variantIndex) => {
              const color = String(variant?.color || '').trim();
              const sizeSummary = variant?.size_quantities
                ? Object.entries(variant.size_quantities).map(([size, qty]) => `${size}: ${qty}`).join(', ')
                : '';
              return (
                <div key={`variant-edit-${variantIndex}`} className="list-item product-variant-card">
                  <div className="row product-variant-head">
                    <div>
                      <strong>Variant {variantIndex + 1}</strong>
                      <div className="muted">{color || 'No color selected'}</div>
                    </div>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => removeVariant(variantIndex)}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="grid">
                    <div>
                      <label>Color</label>
                      <input
                        value={color}
                        onChange={(e) => updateVariant(variantIndex, 'color', e.target.value)}
                        placeholder="e.g. Black"
                      />
                    </div>
                    <div>
                      <label>Pack Count (Total Qty)</label>
                      <input
                        type="number"
                        value={variant?.pack_count ?? variant?.quantity ?? 0}
                        onChange={(e) => updateVariant(variantIndex, 'pack_count', e.target.value)}
                      />
                    </div>
                    <div>
                      <label>Auto Size Split</label>
                      <div className="muted">
                        {sizeSummary || 'Select sizes and pack count to auto split'}
                      </div>
                    </div>
                  </div>

                  <div className="product-chip-block">
                    <label>Sizes</label>
                    <div className="product-chip-row">
                      {sizeOptions.map((size) => {
                        const selected = (variant?.sizes || []).includes(size);
                        return (
                          <button
                            key={`edit-${variantIndex}-${size}`}
                            type="button"
                            className={`chip-toggle ${selected ? 'active' : ''}`}
                            onClick={() => toggleSize(variantIndex, size)}
                          >
                            {size}
                          </button>
                        );
                      })}
                    </div>
                    {sizeOptions.length === 0 ? (
                      <div className="muted">Select category like Men/Women/Kids/Sports to enable size presets.</div>
                    ) : null}
                  </div>

                  <div>
                    <label>Color Image</label>
                    {variant?.image ? (
                      <div className="product-thumb" style={{ marginTop: 8, marginBottom: 8 }}>
                        <img src={getMediaUrl(variant.image)} alt={color || 'variant'} />
                      </div>
                    ) : (
                      <div className="muted" style={{ marginTop: 8, marginBottom: 8 }}>No image for this color</div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setVariantColorFile(variantIndex, e.target.files?.[0] || null)}
                    />
                    {variantColorFiles[String(variantIndex)]?.name ? (
                      <div className="muted" style={{ marginTop: 6 }}>
                        Selected: {variantColorFiles[String(variantIndex)].name}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <button onClick={handleSave}>Save Changes</button>
        {message ? <span className="muted">{message}</span> : null}
      </div>
    </div>
  );
};

export default ProductEditPage;
